"use client";

import { useHeaderState } from "@faire/deprecated-retailer-visitor-shared/components/Header/useHeaderState";
import { LazyResultsDropdown } from "@faire/deprecated-retailer-visitor-shared/components/TopSearch/ResultDropdown/index";
import { ResultDropdownBackgroundOverlay } from "@faire/deprecated-retailer-visitor-shared/components/TopSearch/ResultDropdown/ResultDropdownBackgroundOverlay";
import { SearchInput } from "@faire/deprecated-retailer-visitor-shared/components/TopSearch/SearchInput";
import {
  CancelButton,
  IconWrapper,
  TopSearchComponent,
  TopSearchWrapper,
} from "@faire/deprecated-retailer-visitor-shared/components/TopSearch/styled";
import { SearchForProduct } from "@faire/deprecated-retailer-visitor-shared/events";
// eslint-disable-next-line @faire/enforce-internal-folder-encapsulation
import { SearchSuggestionsThrottledQueryStringProvider } from "@faire/deprecated-retailer-visitor-shared/features/SearchSuggestions-v2/DataFetching/__internal__/useSearchSuggestionsThrottledQueryStringContext";
// eslint-disable-next-line @faire/enforce-internal-folder-encapsulation
import { SearchSuggestionsUnthrottledQueryStringProvider } from "@faire/deprecated-retailer-visitor-shared/features/SearchSuggestions-v2/DataFetching/__internal__/useSearchSuggestionsUnthrottledQueryStringContext";
import { useIsLoggedInRetailer } from "@faire/deprecated-retailer-visitor-shared/lib/isLoggedInRetailer";
import { useRetailerRoutingContext } from "@faire/deprecated-retailer-visitor-shared/RetailerRoutingContextProvider";
import {
  useAssignSettingEnableNewSearchSuggestionsEmptyState,
  useSettingEnableNewSearchSuggestionsEmptyState,
} from "@faire/deprecated-retailer-visitor-shared/settings/useSettingEnableNewSearchSuggestionsEmptyState";
import { useSettingMultimodalProductSearch } from "@faire/deprecated-retailer-visitor-shared/settings/useSettingMultimodalProductSearch";
import { Core } from "@faire/slate/foundation/color";
import { Close } from "@faire/slate/icons/Close";
import { Search } from "@faire/slate/icons/Search";
import productSearch from "@faire/web-api--source/routes/www/search";
import { debounceIfPossible } from "@faire/web/common/globals/debounceIfPossible";
import { getDocumentOrThrow } from "@faire/web/common/globals/getDocument";
import { getWindow } from "@faire/web/common/globals/getWindow";
import { useViewport } from "@faire/web/common/hooks/useViewport";
import { useStrictLocalization } from "@faire/web/common/localization";
import { prependLocaleToUrl } from "@faire/web/common/routing/util";
import { useScrollStore } from "@faire/web/common/ScrollStore";
import { trackEvent } from "@faire/web/common/trackEvent";
import { isNotUndefined } from "@faire/web/common/typescriptUtils";
import { Spacer } from "@faire/web/slate/spacing";
import { useEffectEvent } from "@faire/web/ui/hooks/useEffectEvent";
import { StrictLocalMsg } from "@faire/web/ui/lib/localization";
import React, {
  Suspense,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { useHistory } from "react-router-dom";

import { LazyMultimodalSearchInput } from "./MultimodalSearchInput";

interface IProps {
  autofocus: boolean;
  onFocus?: (element: HTMLDivElement | null, isOnToggle?: boolean) => void;
  onBlur?: () => void;
  inputId: string;
  inputDataTestId: string;
  placeholder?: string;
  shouldShowLoggedOutSearchSuggestion?: boolean;
  searchPageProductSearchString?: string;
  showTabletFullWidth?: boolean;
}

export const TopSearch = ({
  inputId,
  onFocus,
  onBlur,
  autofocus,
  inputDataTestId,
  placeholder,
  shouldShowLoggedOutSearchSuggestion,
  searchPageProductSearchString,
  showTabletFullWidth,
}: IProps) => {
  const enableNewSearchSuggestionsEmptyState =
    useSettingEnableNewSearchSuggestionsEmptyState();
  const assignSettingEnableNewSearchSuggestionsEmptyState =
    useAssignSettingEnableNewSearchSuggestionsEmptyState();
  const { isTabletAndBelow, isMobile, isDesktopAndAbove } = useViewport();

  const routingContext = useRetailerRoutingContext();
  /* eslint-disable-next-line react-compiler/react-compiler */
  const { useRouterOrHistory = useHistory } = routingContext;
  /* eslint-disable-next-line react-compiler/react-compiler */
  const router = useRouterOrHistory();

  const { strictLocalize } = useStrictLocalization();
  const isLoggedInRetailer = useIsLoggedInRetailer();

  const { pushNoScroll, popNoScroll } = useScrollStore([
    "pushNoScroll",
    "popNoScroll",
  ]);

  const isMultimodal = useSettingMultimodalProductSearch();

  const elementRef = useRef<HTMLDivElement>(null);
  const [searchElement, setSearchElement] = useState<HTMLElement | null>(null);

  const {
    query: searchQuery,
    isSearchVisible,
    setSearchQuery,
    showSearchQuery,
    hideSearchQuery,
    clearSearchQuery,
  } = useHeaderState();

  const init = useEffectEvent(() => {
    if (searchQuery && !shouldShowLoggedOutSearchSuggestion) {
      onValueChange(searchQuery);
    }

    const isAlreadySelected =
      searchElement != null &&
      searchElement === getWindow()?.document.activeElement;

    if (autofocus || isAlreadySelected) {
      focus();
    }
  });

  useEffect(() => {
    return init();
  }, [init]);

  useEffect(() => {
    if (isSearchVisible && searchQuery.length === 0) {
      assignSettingEnableNewSearchSuggestionsEmptyState();
    }
  }, [
    assignSettingEnableNewSearchSuggestionsEmptyState,
    isSearchVisible,
    searchQuery,
  ]);

  const onValueChange = useCallback(
    (searchValue: string, isDelete: boolean = false) => {
      if (searchValue === searchQuery && !isDelete) {
        return;
      }

      setSearchQuery(searchValue);
    },
    [searchQuery, setSearchQuery]
  );

  const focus = useCallback(
    (isOnToggle?: boolean) => {
      const topSearchElement = getDocumentOrThrow().getElementById(inputId);
      if (topSearchElement) {
        topSearchElement.focus();
      }
      onFocus?.(elementRef.current, isOnToggle);
    },
    [inputId, onFocus]
  );

  const isInitialRender = useRef(true);
  useEffect(() => {
    // Don't call focus/blur callbacks on initial render
    if (isInitialRender.current) {
      isInitialRender.current = false;
    } else {
      if (isSearchVisible) {
        focus();
      } else {
        onBlur?.();
      }
    }
  }, [isSearchVisible, focus, onBlur]);

  const scrollLockRef = useRef(false);
  useEffect(() => {
    if (isSearchVisible) {
      if (!scrollLockRef.current) {
        pushNoScroll();
        scrollLockRef.current = true;
      }
    } else {
      if (scrollLockRef.current) {
        popNoScroll();
        scrollLockRef.current = false;
      }
    }

    // Cleanup on unmount or when dependencies change
    return () => {
      // Always attempt to unlock scroll if it was locked
      if (scrollLockRef.current) {
        popNoScroll();
        scrollLockRef.current = false;
      }
    };
  }, [isSearchVisible, pushNoScroll, popNoScroll]);

  useEffect(() => {
    const activeElementId = getDocumentOrThrow().activeElement?.id;
    const searchIdList = ["top-search", "tablet-top-search"];
    if (!isMobile && !searchIdList.includes(activeElementId || "")) {
      hideSearchQuery();
    }
  }, [isMobile, hideSearchQuery]);

  useEffect(() => {
    if (shouldShowLoggedOutSearchSuggestion) {
      setSearchQuery("");
    } else {
      setSearchQuery(searchPageProductSearchString ?? "");
    }
  }, [
    searchPageProductSearchString,
    setSearchQuery,
    shouldShowLoggedOutSearchSuggestion,
  ]);

  const shouldShowResetIcon = !!searchQuery || isDesktopAndAbove;

  const onClickOutside = useCallback(() => {
    // There are cases where a click outside can be detected but the
    // search bar still has focus. E.g. if they mousedown within the search
    // bar to select some text, and then mouseup outside of the search bar.
    // In these cases the browser properly keeps focus on the search input, and
    // we should make sure to not shut down the search bar and suggestions due
    // to the outside detected click.
    const el = getDocumentOrThrow().activeElement;
    if (el?.id === inputId) {
      return;
    }
    if (isSearchVisible) {
      hideSearchQuery();
    }
  }, [hideSearchQuery, inputId, isSearchVisible]);

  const trackSearch = useCallback((event: string) => {
    trackEvent(`/searchbar${event}`, true);
  }, []);

  const debouncedTrackSearch = useMemo(
    () => debounceIfPossible((event: string) => trackSearch(event), 400),
    [trackSearch]
  );

  const onChange = useCallback(
    (value: string) => {
      debouncedTrackSearch(`/type/${value}`);
      onValueChange(value);
    },
    [debouncedTrackSearch, onValueChange]
  );

  const toggle = useCallback(() => {
    if (!isSearchVisible) {
      onValueChange(searchQuery || "");
      focus(true);
    }

    return isSearchVisible ? hideSearchQuery() : showSearchQuery();
  }, [
    focus,
    hideSearchQuery,
    isSearchVisible,
    onValueChange,
    searchQuery,
    showSearchQuery,
  ]);

  const dontShowSearch = useCallback(() => {
    if (isSearchVisible) {
      toggle();
    }
  }, [toggle, isSearchVisible]);

  const submit = useCallback(
    (
      query: string | undefined,
      submittedUrl?: string,
      navigate: boolean = true
    ) => {
      trackSearch("/submit");
      const isLegitQuery = isNotUndefined(query) && query.trim().length > 0;

      if (query) {
        onValueChange(query);
      } else {
        onValueChange("");
        clearSearchQuery();
      }

      if (isLegitQuery) {
        SearchForProduct.publish(query);
      }

      dontShowSearch();

      if (navigate) {
        if (submittedUrl) {
          router.push(prependLocaleToUrl(submittedUrl, routingContext));
        } else if (isLegitQuery) {
          router.push(
            prependLocaleToUrl(
              productSearch({ q: searchQuery }),
              routingContext
            )
          );
        }
      }
    },
    [
      clearSearchQuery,
      dontShowSearch,
      router,
      onValueChange,
      routingContext,
      searchQuery,
      trackSearch,
    ]
  );

  const onSubmit = useCallback(() => {
    submit(searchQuery);
  }, [searchQuery, submit]);

  const onCancel = useCallback(
    (dontCloseOnMobile?: boolean) => {
      trackSearch("/cancel");
      clearSearchQuery();
      onValueChange("");
      if (!isMobile || !dontCloseOnMobile) {
        dontShowSearch();
      } else {
        focus();
      }
    },
    [
      clearSearchQuery,
      dontShowSearch,
      focus,
      isMobile,
      onValueChange,
      trackSearch,
    ]
  );

  const showSearch = useCallback(() => {
    if (!isSearchVisible) {
      toggle();
    }
  }, [isSearchVisible, toggle]);

  const handleInputHover = useCallback(() => {
    trackSearch("/hover");
  }, [trackSearch]);

  const handleInputFocus = useCallback(() => {
    trackSearch("/focus");
    showSearch();
  }, [showSearch, trackSearch]);

  const SearchInputComponent = isMultimodal
    ? LazyMultimodalSearchInput
    : SearchInput;

  const displaySDUIEmptyState =
    isLoggedInRetailer &&
    enableNewSearchSuggestionsEmptyState &&
    isSearchVisible &&
    searchQuery.length === 0;

  const shouldEnableSuggestionsImpressionAllocationV2 =
    isDesktopAndAbove && isLoggedInRetailer;
  const showBackgroundOverlay =
    isTabletAndBelow || shouldEnableSuggestionsImpressionAllocationV2;

  const tabletFullWidth = isLoggedInRetailer || showTabletFullWidth;

  return (
    <TopSearchWrapper
      onClickOutside={onClickOutside}
      $tabletFullWidth={tabletFullWidth}
    >
      <TopSearchComponent
        className={
          isSearchVisible
            ? "topsearch-component expanded"
            : "topsearch-component"
        }
        ref={elementRef}
        $isSearchVisible={isSearchVisible}
        $isMultimodal={isMultimodal}
      >
        <SearchInputComponent
          id={inputId}
          inputDataTestId={inputDataTestId}
          searchValue={searchQuery}
          placeholder={placeholder ?? ""}
          aria-label={strictLocalize({
            id: "z7d8Af",
            defaultMessage: "Search products or brands",
            description: {
              text: "Text for search box.",
            },
          })}
          onSearchChange={onChange}
          onReturnPressed={onSubmit}
          onEscapePressed={onCancel}
          getRef={setSearchElement}
          onClick={showSearch}
          onFocus={handleInputFocus}
          onHover={handleInputHover}
        />
        <SearchInputIcon
          isSearchVisible={isSearchVisible}
          shouldShowResetIcon={shouldShowResetIcon}
          showSearch={showSearch}
          onCancel={onCancel}
        />
        {isSearchVisible && !showTabletFullWidth ? (
          <ResultDropdownBackgroundOverlay
            showBackgroundOverlay={showBackgroundOverlay}
            onClick={() => onCancel()}
          />
        ) : null}
        {isSearchVisible ? (
          <Suspense fallback={null}>
            <SearchSuggestionsUnthrottledQueryStringProvider
              searchValue={searchQuery}
            >
              <SearchSuggestionsThrottledQueryStringProvider
                searchValue={searchQuery}
              >
                <LazyResultsDropdown
                  displaySDUIEmptyState={displaySDUIEmptyState}
                  parent={elementRef.current}
                  searchElement={searchElement}
                  submit={submit}
                  cancelSearch={onCancel}
                  query={searchQuery}
                  dontShowSearch={dontShowSearch}
                  shouldEnableSuggestionsImpressionAllocationV2={
                    shouldEnableSuggestionsImpressionAllocationV2
                  }
                />
              </SearchSuggestionsThrottledQueryStringProvider>
            </SearchSuggestionsUnthrottledQueryStringProvider>
          </Suspense>
        ) : null}
      </TopSearchComponent>
      {isSearchVisible && isTabletAndBelow ? (
        <>
          {!isMobile ? <Spacer size="1x" /> : null}
          <CancelButton onClick={() => onCancel()}>
            <StrictLocalMsg
              id="dmR9HR"
              defaultMessage="Cancel"
              description={{ text: "Clear search query" }}
            />
          </CancelButton>
        </>
      ) : null}
    </TopSearchWrapper>
  );
};

const SearchInputIcon: React.FC<{
  isSearchVisible: boolean;
  shouldShowResetIcon: boolean;
  showSearch: () => void;
  onCancel: (dontCloseOnMobile?: boolean) => void;
}> = ({ isSearchVisible, shouldShowResetIcon, showSearch, onCancel }) => {
  if (!isSearchVisible) {
    return (
      <IconWrapper onClick={showSearch} $padding="0 20px 0 12px">
        <Search color={Core.icon.primary} strokeWidth={2} />
      </IconWrapper>
    );
  }
  if (shouldShowResetIcon) {
    return (
      <IconWrapper
        onClick={() => onCancel(true)}
        $padding="0 20px 0 12px"
        data-test-id="close-search"
      >
        <Close color={Core.icon.primary} />
      </IconWrapper>
    );
  }
  return null;
};
